import React, { useState } from 'react';
import { AlertTriangle, Download, Users, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { apiService } from '@/services/api';
import { exportRegeneratedPasswordsToExcel } from '@/utils/exportUtils';
import type { RegeneratePasswordsResult } from '@/services/api';

interface RegeneratedUser {
  name: string;
  role: string;
  newPassword: string;
}

export function PasswordRegeneration() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<RegeneratePasswordsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRegeneratePasswords = async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiService.regenerateSupervisorMonitorPasswords();
      setResult(response);
      
      if (response.success) {
        // Automatically trigger Excel download
        const filename = `parole-regenerate-${new Date().toISOString().split('T')[0]}.xlsx`;
        await exportRegeneratedPasswordsToExcel(response.regeneratedUsers, filename);
      }
    } catch (err: unknown) {
      console.error('Error regenerating passwords:', err);
      setError((err instanceof Error ? err.message : String(err)) || 'A apărut o eroare la regenerarea parolelor');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!result?.regeneratedUsers) return;

    try {
      const filename = `parole-regenerate-${new Date().toISOString().split('T')[0]}.xlsx`;
      await exportRegeneratedPasswordsToExcel(result.regeneratedUsers, filename);
    } catch (err: unknown) {
      console.error('Error downloading Excel:', err);
      setError('A apărut o eroare la descărcarea fișierului Excel');
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Regenerare Parole Supervizori și Monitori
        </CardTitle>
        <CardDescription>
          Generează parole noi pentru toți supervisorii și monitorii și descarcă lista în format Excel
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warning Alert */}
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Atenție:</strong> Această operațiune va genera parole noi pentru toți supervisorii și monitorii existenți. 
            Parolele vechi vor fi invalidate și utilizatorii vor trebui să folosească noile parole pentru a se conecta.
          </AlertDescription>
        </Alert>

        {/* Action Button */}
        <div className="flex gap-4">
          <Button 
            onClick={handleRegeneratePasswords}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Users className="h-4 w-4" />
            )}
            {isLoading ? 'Se generează parolele...' : 'Regenerează Parole și Descarcă Excel'}
          </Button>

          {result?.success && (
            <Button 
              variant="outline" 
              onClick={downloadExcel}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Descarcă Excel din nou
            </Button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Result */}
        {result && (
          <div className="space-y-4">
            {result.success ? (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Succes!</strong> {result.message}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{result.message}</AlertDescription>
              </Alert>
            )}

            {/* Display Generated Users */}
            {result.regeneratedUsers && result.regeneratedUsers.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700">
                  Utilizatori cu parole regenerate ({result.regeneratedUsers.length}):
                </h4>
                <div className="grid gap-2 max-h-40 overflow-y-auto">
                  {result.regeneratedUsers.map((user, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="font-medium text-sm">{user.name}</div>
                        <Badge variant={user.role === 'supervisor' ? 'default' : 'secondary'}>
                          {user.role === 'supervisor' ? 'Supervisor' : 'Monitor'}
                        </Badge>
                      </div>
                      <div className="font-mono text-sm bg-white px-2 py-1 rounded border">
                        {user.newPassword}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  💡 Parolele sunt afișate aici pentru verificare, dar au fost salvate și în fișierul Excel descărcat.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}